import React, { useEffect, useRef, useState } from "react";
import { AlertOctagon, Loader2, CheckCircle2, RefreshCw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { errMsg } from "../hq/hqUtils";

/* /admin/system/reset , one-shot wipe + archive button.
   Snapshots every customer / order / HQ collection into archived_* copies,
   then empties the live collections so the new build sits brand new.
   Idempotent: re-running just no-ops on already-empty live tables. */
const AUBERGINE = "#3E1F46";
const PLUM = "#5B2A4F";
const DANGER = "#A33030";

export default function AdminResetSystem() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  // Restore state
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  const resultRef = useRef(null);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const [r, rr] = await Promise.all([
        api.get("/admin/reset-system/preview"),
        api.get("/admin/reset-system/restore-preview").catch(() => ({ data: null })),
      ]);
      setPreview(r.data);
      setRestorePreview(rr?.data);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't load preview."));
    } finally { setLoading(false); }
  };
  useEffect(() => { loadPreview(); }, []);

  const run = async () => {
    if (confirmText.trim().toUpperCase() !== "WIPE") {
      toast.error("Type WIPE in the confirmation box first.");
      return;
    }
    setRunning(true);
    try {
      const r = await api.post("/admin/reset-system?confirm=WIPE");
      setResult(r.data);
      toast.success(`Wiped ${r.data.rows_wiped} rows. System is now brand new.`);
      // Scroll the success panel into view so the user doesn't think nothing happened.
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
      loadPreview();
    } catch (e) {
      console.error("Reset failed:", e);
      toast.error(errMsg(e, "Reset failed , check your network + try again."));
    } finally {
      setRunning(false);
      setConfirmText("");
    }
  };

  const runRestore = async () => {
    if (restoreConfirm.trim().toUpperCase() !== "RESTORE") {
      toast.error("Type RESTORE in the confirmation box first.");
      return;
    }
    if (!window.confirm("Restore every archived collection back into the live tables? Any current live data will be replaced.")) return;
    setRestoring(true);
    try {
      const r = await api.post("/admin/reset-system/restore?confirm=RESTORE");
      setRestoreResult(r.data);
      toast.success(`Restored ${r.data.rows_restored} rows from archive.`);
      loadPreview();
    } catch (e) {
      console.error("Restore failed:", e);
      toast.error(errMsg(e, "Restore failed , check your network + try again."));
    } finally {
      setRestoring(false);
      setRestoreConfirm("");
    }
  };

  return (
    <div data-testid="admin-reset-system" className="space-y-6 max-w-3xl">
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: DANGER }}>
          ✦ DESTRUCTIVE
        </div>
        <h1 className="font-serif text-4xl mt-1.5" style={{ color: AUBERGINE }}>
          Reset System
        </h1>
        <p className="font-serif italic mt-1.5 text-[#6E5849] max-w-2xl text-sm leading-relaxed">
          One-shot button to archive every customer, order, HQ login and message into{" "}
          <code className="font-mono text-xs">archived_*</code> copies, then empty the live
          collections so the build sits brand new. The Historical Customer Data tab
          continues to read from the archives so nothing is actually lost.
        </p>
      </header>

      <div className="p-4 bg-[#FFF4F0] border-2 rounded-md text-sm leading-relaxed" style={{ borderColor: "#E8B5A2", color: DANGER }}>
        <div className="flex items-start gap-2">
          <AlertOctagon className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <strong>What this does</strong>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>Customers tab → <strong>0 customers</strong></li>
              <li>All order tabs → <strong>0 orders</strong></li>
              <li>Vouchers, credits, refund notes, email outbox → <strong>0</strong></li>
              <li>Every existing HQ login <strong>stops working</strong> (passwords archived)</li>
              <li>Historical Customer Data tab keeps showing everything (reads from archives)</li>
              <li><strong>Wipes the current environment only.</strong> If you&apos;re on preview, only preview data goes. If you&apos;re on <code>heritagedahlias.nz</code>, this wipes production. Deploy this build to prod and click the same button there to reset production.</li>
            </ul>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-[#7A6A55]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Counting live rows…</div>
      ) : (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "#E0D1C8" }}>
          <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "#E0D1C8", backgroundColor: "#FBF6E9" }}>
            <div className="text-sm font-serif" style={{ color: AUBERGINE }}>
              <strong>{preview?.rows_to_wipe ?? 0}</strong> rows currently live across{" "}
              <strong>{preview?.collections?.length ?? 0}</strong> collections
            </div>
            <button onClick={loadPreview} className="text-[10px] tracking-[0.2em] uppercase font-semibold inline-flex items-center gap-1 text-[#7A6A55] hover:text-[#3E1F46]">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          <ul className="text-xs divide-y" style={{ borderColor: "#F0E6D2" }}>
            {(preview?.collections || []).map((c) => (
              <li key={c.live} className="px-4 py-1.5 grid grid-cols-3 gap-2" style={{ borderColor: "#F0E6D2" }}>
                <span className="font-mono text-[#3C2A1E]">{c.live}</span>
                <span className="text-right text-[#A33030] font-semibold">{c.live_count} live</span>
                <span className="text-right text-[#7A6A55]">{c.archive_count} archived</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Confirmation gate */}
      <div className="p-4 border-2 rounded-md" style={{ borderColor: DANGER, backgroundColor: "#FFF4F0" }}>
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mb-2" style={{ color: DANGER }}>
          Type <code className="font-mono">WIPE</code> to confirm
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder="Type WIPE"
            data-testid="reset-confirm-input"
            disabled={running}
            className="flex-1 px-3 py-2 bg-white border rounded font-mono text-sm uppercase tracking-[0.2em]"
            style={{ borderColor: DANGER, color: DANGER }}
          />
          <button
            type="button"
            onClick={run}
            disabled={running || confirmText.trim().toUpperCase() !== "WIPE"}
            data-testid="reset-confirm-button"
            className="px-5 py-2 rounded text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: DANGER, color: "white" }}
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertOctagon className="w-3.5 h-3.5" />}
            {running ? "Wiping…" : "Wipe + Archive"}
          </button>
        </div>
      </div>

      {result && (
        <div ref={resultRef} className="p-4 bg-[#EBF3E5] border rounded-md text-sm" style={{ borderColor: "#C8DCB6", color: "#2F4A2A" }} data-testid="reset-result">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-4 h-4" /> <strong>Wipe complete , {result.rows_wiped} rows archived & cleared.</strong>
          </div>
          <details className="mt-2 text-xs">
            <summary className="cursor-pointer underline">Per-collection breakdown</summary>
            <ul className="mt-1 space-y-0.5 font-mono">
              {(result.results || []).map((r, i) => (
                <li key={i}>{r.collection}: {r.archived ?? 0} archived → {r.wiped ?? 0} wiped {r.error ? ` (error: ${r.error})` : ""}</li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {/* Restore last archive , soft-undo for a fat-fingered wipe. */}
      <div className="mt-8 p-4 border-2 rounded-md" style={{ borderColor: "#C9A268", backgroundColor: "#FFF9EE" }} data-testid="restore-panel">
        <div className="flex items-start gap-2">
          <Undo2 className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "#8A6B25" }} />
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.35em] uppercase font-semibold" style={{ color: "#8A6B25" }}>
              ✦ Soft undo
            </div>
            <h2 className="font-serif text-2xl mt-1" style={{ color: AUBERGINE }}>Restore last archive</h2>
            <p className="font-serif italic mt-1 text-[#6E5849] text-sm leading-relaxed">
              Copies every <code className="font-mono text-xs">archived_*</code> collection back into its live twin.
              Any current live data is wiped first so you get an exact snapshot restore.
              Safe to re-run , the archive is never touched.
            </p>
            {restorePreview && (
              <div className="mt-3 text-xs" style={{ color: "#6E5849" }}>
                <strong>{restorePreview.rows_to_restore}</strong> archived rows ready to restore across{" "}
                <strong>{restorePreview.collections?.length ?? 0}</strong> collections.
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <input
                type="text"
                value={restoreConfirm}
                onChange={(e) => setRestoreConfirm(e.target.value.toUpperCase())}
                placeholder="Type RESTORE"
                data-testid="restore-confirm-input"
                disabled={restoring}
                className="flex-1 px-3 py-2 bg-white border rounded font-mono text-sm uppercase tracking-[0.2em]"
                style={{ borderColor: "#C9A268", color: "#8A6B25" }}
              />
              <button
                type="button"
                onClick={runRestore}
                disabled={restoring || restoreConfirm.trim().toUpperCase() !== "RESTORE" || !restorePreview?.rows_to_restore}
                data-testid="restore-confirm-button"
                className="px-5 py-2 rounded text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-1.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: "#8A6B25", color: "white" }}
              >
                {restoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                {restoring ? "Restoring…" : "Restore from archive"}
              </button>
            </div>
            {restoreResult && (
              <div className="mt-3 p-3 bg-[#EBF3E5] border rounded text-xs" style={{ borderColor: "#C8DCB6", color: "#2F4A2A" }} data-testid="restore-result">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4" /> <strong>Restore complete , {restoreResult.rows_restored} rows pulled back from archive.</strong>
                </div>
                <details className="mt-1">
                  <summary className="cursor-pointer underline">Per-collection breakdown</summary>
                  <ul className="mt-1 space-y-0.5 font-mono">
                    {(restoreResult.results || []).map((r, i) => (
                      <li key={i}>{r.collection}: cleared {r.cleared_live ?? 0} → restored {r.restored ?? 0} {r.error ? ` (error: ${r.error})` : ""}</li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
